<script setup lang="ts" generic="TValue">
import { computed, type CSSProperties, inject, useAttrs, useTemplateRef } from "vue";
import type { CheckboxCheckmark, Placement } from "../models/types";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcCheckboxProps<TProp> = {
  /** Specifies the checkbox's checkmark style */
  checkmark?: CheckboxCheckmark;
  /** Disables checkbox for selection */
  disabled?: boolean;
  /** Specifies custom `false` value for the checkbox */
  falseValue?: TProp;
  /** Specifies the label of the checkbox */
  label?: string | number | boolean;
  /** Specifies the position of the checkbox's label */
  labelPlacement?: Placement;
  /** Specifies the name of the checkbox */
  name?: string;
  /** Specifies that checkbox is read-only */
  readonly?: boolean;
  /** Makes the checkbox's border rounded */
  rounded?: boolean;
  /** Specifies custom `true` value for the checkbox */
  trueValue?: TProp;
  /** Specifies the value of the checkbox */
  value?: TProp;
};

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

/** Specifies two-way binding for the checkbox */
const modelValue = defineModel<TValue | boolean | TValue[]>();

const {
  checkmark = "square",
  disabled = false,
  falseValue = false,
  label = undefined,
  labelPlacement = "right",
  name,
  readonly = false,
  rounded = false,
  trueValue = true,
  value,
} = defineProps<BcCheckboxProps<TValue>>();

/** Component container reference */
const containerRef = useTemplateRef("containerRef");
/** Checkbox input reference */
const inputRef = useTemplateRef("inputRef");

/** Gets if the checkbox is checked based on the `true` and `false` values */
const isChecked = computed(() => {
  if (Array.isArray(modelValue.value) && value) {
    return value !== undefined && modelValue.value.some((v) => v === value);
  }
  return typeof modelValue.value !== "undefined" && modelValue.value !== null
    ? modelValue.value === trueValue
    : value === trueValue;
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as CSSProperties);
const inheritedAttrs = computed(() => {
  const { class: _, style: __, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

function focus(): void {
  // MacOS FireFox and Safari do not focus when clicked
  inputRef.value?.focus();
}

function updateInput(event: Event): void {
  if (readonly) {
    return;
  }

  let inputChecked = (event.target as HTMLInputElement).checked;
  if (Array.isArray(modelValue.value) && value !== undefined && value !== null) {
    let newValue = [...modelValue.value];
    if (inputChecked) {
      newValue.push(value);
    } else {
      newValue.splice(newValue.indexOf(value), 1);
    }
    modelValue.value = newValue;
  } else {
    modelValue.value = inputChecked ? trueValue : falseValue;
  }
}

defineExpose({
  modelValue,
  containerRef,
  inputRef,
  isChecked,
});
</script>

<template>
  <label
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls checkboxControl"
    v-bind:class="[
      `label-${labelPlacement}`,
      `checkmark-${checkmark}`,
      {
        disabled: disabled,
        readonly: readonly,
        rounded: rounded,
      },
      ...rootClass,
    ]"
    v-bind:style="rootStyle"
    ref="containerRef"
    v-on:click="focus"
    v-on:keydown.prevent.enter="containerRef!.click()"
    v-on:keydown.prevent.space="containerRef!.click()"
  >
    <input
      v-show="!readonly"
      type="checkbox"
      ref="inputRef"
      v-on:click.stop
      v-bind:disabled="disabled"
      v-bind:readonly="readonly"
      v-bind:name="name"
      v-bind:checked="isChecked"
      v-bind:value="value"
      v-bind="inheritedAttrs"
      v-on:change="updateInput"
    />
    <span
      class="checkboxLabel"
      v-bind:class="{ readonly: readonly }"
      v-bind:data-locid="$attrs['data-label-locid']"
      v-bind:data-locparam="$attrs['data-label-locparam']"
      v-bind:data-locparam1="$attrs['data-label-locparam1']"
      v-bind:data-locparam2="$attrs['data-label-locparam2']"
    >
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-checkbox.less";
</style>
